import type { CaseFile, PresignedFileUrl } from "./types";
import type { FileUpload } from "../../components/inputs/types";

/** Remove the first entry whose filename matches `name` (leaves others intact). */
export function removeFirstByName(
  files: (FileUpload | CaseFile)[],
  name: string
): (FileUpload | CaseFile)[] {
  const idx = files.findIndex((f) =>
    "file" in f ? f.file.name === name : (f as CaseFile).filename === name
  );
  if (idx === -1) return files;
  return files.filter((_, i) => i !== idx);
}

/** Map a presign response to file_id → url (null when signing that file failed). */
export function presignedUrlsToMap(
  urls: PresignedFileUrl[]
): Record<number, string | null> {
  return Object.fromEntries(urls.map((u) => [u.file_id, u.url ?? null]));
}

/**
 * Open a URL that must be fetched asynchronously (e.g. a presigned download
 * URL) in a new tab. The window is opened synchronously so the browser ties
 * it to the user gesture, then navigated once the URL resolves.
 */
export async function openFileInNewTab(
  getUrl: () => Promise<string | null | undefined>
): Promise<void> {
  const win = window.open("", "_blank");
  if (win) {
    try {
      win.document.body.textContent = "Loading preview…";
    } catch {
      // cross-origin or closed window — nothing to show
    }
  }
  let url: string | null | undefined;
  try {
    url = await getUrl();
  } catch {
    win?.close();
    return;
  }
  if (!url) {
    win?.close();
    return;
  }
  if (win) {
    win.opener = null;
    win.location.href = url;
  } else {
    window.open(url, "_blank", "noopener,noreferrer");
  }
}

export function showUploadResult(
  result: { finalizeMessage?: string },
  successMessage: string,
  showSuccess: (msg: string) => void
) {
  showSuccess(result.finalizeMessage || successMessage);
}
